import React, { useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

import ButtonComponent from '../../../../components/common/button/Button';
import DataTable from '../../../../components/common/datatable/DataTable';

const TargetValueDetails = (props) => {
  const [isGridReady, setIsGridReady] = useState(false);
  const [disableFilterBtn, setDisableFilterBtn] = useState(true);
  const [selectedColumn, setSelectedColumn] = useState('');

  const renderExpandableText = (params, maxLength = 50) => {
    const text = params.value || ""; // The error details text
    const isLongText = text.length > maxLength;

    // Initialize the showFullText property for the row if not already set
    if (!params.node.showFullText) {
      params.node.showFullText = false;
    }

    const toggleText = () => {
      params.node.showFullText = !params.node.showFullText;
      params.api.refreshCells({ rowNodes: [params.node], force: true }); // Refresh the cell to update the UI
    };

    return isLongText ? (
      <div>
        {params.node.showFullText ? (
          <>
            <span>{text}</span>{" "}
            <button
              onClick={toggleText}
              style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              Less
            </button>
          </>
        ) : (
          <>
            <span>{text.substring(0, maxLength)}...</span>{" "}
            <button
              onClick={toggleText}
              style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              More
            </button>
          </>
        )}
      </div>
    ) : (
      <span>{text}</span>
    );
  };

  const colDefData = useMemo(() => {
   return [
      {
        headerName: "Contract Serial Number",
        field: "contractSerialNumber",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Agreement Number",
        field: "agreementNumber",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Agreement Date",
        field: "agreementDate",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        // provide a JS Date for AG Grid sorting/filtering
        valueGetter: (params) => {
          const v = params.data?.agreementDate;
          if (!v || typeof v !== 'string') return null;
          const parts = v.split('-'); // expecting "DD-MM-YYYY"
          if (parts.length !== 3) return null;
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          const d = new Date(year, month, day);
          return isNaN(d.getTime()) ? null : d;
        },

        // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
        valueFormatter: (params) => {
          const src = params.data?.agreementDate;
          if (src && typeof src === 'string') return src;
          if (params.value instanceof Date && !isNaN(params.value.getTime())) {
            const d = params.value;
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}-${mm}-${yyyy}`;
          }
          return "-";
        },

        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue, node) => {
            const src = node?.data?.agreementDate ?? cellValue;
            if (!src) return -1;
            // parse "DD-MM-YYYY"
            const parts = typeof src === 'string' ? src.split('-') : null;
            if (!parts || parts.length !== 3) return -1;
            const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
            if (isNaN(d.getTime())) return -1;
            const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            const filterYMD = new Date(
              filterLocalDateAtMidnight.getFullYear(),
              filterLocalDateAtMidnight.getMonth(),
              filterLocalDateAtMidnight.getDate()
            );
            if (cellYMD.getTime() === filterYMD.getTime()) return 0;
            return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
          },
          browserDatePicker: true,
        },

        // sort by parsing the source string from the row data (robust even if other getters exist)
        sortComparator: (valueA, valueB, nodeA, nodeB) => {
          const aStr = nodeA?.data?.agreementDate ?? valueA;
          const bStr = nodeB?.data?.agreementDate ?? valueB;
          const parse = (s) => {
            if (!s || typeof s !== 'string') return null;
            const p = s.split('-');
            if (p.length !== 3) return null;
            const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
            return isNaN(d.getTime()) ? null : d.getTime();
          };
          const ta = parse(aStr);
          const tb = parse(bStr);
          if (ta === null && tb === null) return 0;
          if (ta === null) return -1;
          if (tb === null) return 1;
          return ta - tb;
        },
      },
      {
        headerName: "Agreement Valid From",
        field: "validityStartDate",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        // provide a JS Date for AG Grid sorting/filtering
        valueGetter: (params) => {
          const v = params.data?.validityStartDate;
          if (!v || typeof v !== 'string') return null;
          const parts = v.split('-'); // expecting "DD-MM-YYYY"
          if (parts.length !== 3) return null;
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          const d = new Date(year, month, day);
          return isNaN(d.getTime()) ? null : d;
        },

        // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
        valueFormatter: (params) => {
          const src = params.data?.validityStartDate;
          if (src && typeof src === 'string') return src;
          if (params.value instanceof Date && !isNaN(params.value.getTime())) {
            const d = params.value;
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}-${mm}-${yyyy}`;
          }
          return "-";
        },

        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue, node) => {
            const src = node?.data?.validityStartDate ?? cellValue;
            if (!src) return -1;
            // parse "DD-MM-YYYY"
            const parts = typeof src === 'string' ? src.split('-') : null;
            if (!parts || parts.length !== 3) return -1;
            const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
            if (isNaN(d.getTime())) return -1;
            const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            const filterYMD = new Date(
              filterLocalDateAtMidnight.getFullYear(),
              filterLocalDateAtMidnight.getMonth(),
              filterLocalDateAtMidnight.getDate()
            );
            if (cellYMD.getTime() === filterYMD.getTime()) return 0;
            return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
          },
          browserDatePicker: true,
        },

        // sort by parsing the source string from the row data (robust even if other getters exist)
        sortComparator: (valueA, valueB, nodeA, nodeB) => {
          const aStr = nodeA?.data?.validityStartDate ?? valueA;
          const bStr = nodeB?.data?.validityStartDate ?? valueB;
          const parse = (s) => {
            if (!s || typeof s !== 'string') return null;
            const p = s.split('-');
            if (p.length !== 3) return null;
            const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
            return isNaN(d.getTime()) ? null : d.getTime();
          };
          const ta = parse(aStr);
          const tb = parse(bStr);
          if (ta === null && tb === null) return 0;
          if (ta === null) return -1;
          if (tb === null) return 1;
          return ta - tb;
        },
      },
      {
        headerName: "Agreement Valid To",
        field: "validityEndDate",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        // provide a JS Date for AG Grid sorting/filtering
        valueGetter: (params) => {
          const v = params.data?.validityEndDate;
          if (!v || typeof v !== 'string') return null;
          const parts = v.split('-'); // expecting "DD-MM-YYYY"
          if (parts.length !== 3) return null;
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          const d = new Date(year, month, day);
          return isNaN(d.getTime()) ? null : d;
        },

        // display the original "DD-MM-YYYY" string if available (keeps editor/text display unchanged)
        valueFormatter: (params) => {
          const src = params.data?.validityEndDate;
          if (src && typeof src === 'string') return src;
          if (params.value instanceof Date && !isNaN(params.value.getTime())) {
            const d = params.value;
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}-${mm}-${yyyy}`;
          }
          return "-";
        },

        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue, node) => {
            const src = node?.data?.validityEndDate ?? cellValue;
            if (!src) return -1;
            // parse "DD-MM-YYYY"
            const parts = typeof src === 'string' ? src.split('-') : null;
            if (!parts || parts.length !== 3) return -1;
            const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
            if (isNaN(d.getTime())) return -1;
            const cellYMD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
            const filterYMD = new Date(
              filterLocalDateAtMidnight.getFullYear(),
              filterLocalDateAtMidnight.getMonth(),
              filterLocalDateAtMidnight.getDate()
            );
            if (cellYMD.getTime() === filterYMD.getTime()) return 0;
            return cellYMD.getTime() < filterYMD.getTime() ? -1 : 1;
          },
          browserDatePicker: true,
        },

        // sort by parsing the source string from the row data (robust even if other getters exist)
        sortComparator: (valueA, valueB, nodeA, nodeB) => {
          const aStr = nodeA?.data?.validityEndDate ?? valueA;
          const bStr = nodeB?.data?.validityEndDate ?? valueB;
          const parse = (s) => {
            if (!s || typeof s !== 'string') return null;
            const p = s.split('-');
            if (p.length !== 3) return null;
            const d = new Date(parseInt(p[2],10), parseInt(p[1],10)-1, parseInt(p[0],10));
            return isNaN(d.getTime()) ? null : d.getTime();
          };
          const ta = parse(aStr);
          const tb = parse(bStr);
          if (ta === null && tb === null) return 0;
          if (ta === null) return -1;
          if (tb === null) return 1;
          return ta - tb;
        },
      },
      {
        headerName: "Plant",
        field: "plantId",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Old Target Value",
        field: "previousTargetValue",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "New Target Value",
        field: "targetValue",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Job ID",
        field: "jobId",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        cellClass: "paymentTerms-cell",
      },
      {
        headerName: "Trigger Type",
        field: "cmType",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Triggered By",
        field: "userId",
        sortable: true,
        resizable: false,
        unSortIcon: true,
      },
      {
        headerName: "Trigger Date",
        field: "lastUpdateTimestamp",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        cellRenderer: (params) => {
            const value = params.value;
            return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '';
        },
      },
      {
        headerName: "Status",
        field: "status",
        pinned: "right",
        sortable: true,
        resizable: false,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        cellClass: "paymentTerms-cell",
        unSortIcon: true,
      },
      {
        headerName: 'Error Details',
        field: "errorDetails",
        sortable: true,
        resizable: false,
        unSortIcon: true,
        initialWidth: 180,
        wrapText: true,
        autoHeight: true,
        cellClass: "status-cell",
        cellRenderer: (params) => renderExpandableText(params, 50)
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.rowData])

  const defaultColDef = {
    sortable: true,
    filter: 'agMultiColumnFilter',
    cellStyle: {
      display: "flex",
      alignItems: "center",
    },
  }

  const gridApiRef = useRef(null);

  const onColumnChange = e => {
    const col = e.target.value;
    setSelectedColumn(col);
    if (col && gridApiRef.current) {
      gridApiRef.current.ensureColumnVisible(col);
    }
  };

  const onGridReady = (params) => {
    gridApiRef.current = params.api; // Store the grid API instance
    setIsGridReady(true);
  };

  useEffect(() => {
    if (isGridReady && gridApiRef?.current) {
      const savedFilterModel = localStorage.getItem("maintenanceReportFilterModel");
      if (savedFilterModel) {
        gridApiRef.current.setFilterModel(JSON.parse(savedFilterModel));
        setDisableFilterBtn(true);
      }
    }
  }, [isGridReady]);

  const selectionColumnDef = useMemo(() => ({
    pinned: "left",
  }), []);

  const clearAllFilters = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null); // Clear all filters
      localStorage.removeItem("maintenanceReportFilterModel");
      setDisableFilterBtn(true);
    }
  };

  const onFilterChanged = () => {
    if (gridApiRef.current) {
      const filterModel = gridApiRef.current.getFilterModel();
      setDisableFilterBtn(Object.keys(filterModel).length === 0); // Enable button if filters exist
      localStorage.setItem("maintenanceReportFilterModel", JSON.stringify(filterModel));
      props.setTargetValueRowsData(gridApiRef.current.getDisplayedRowCount());
    }
  };

  const gridOptions = {
    getRowId: (params) => {
      return `${params?.data?.contractSeqId}`
    },
    overlayLoadingTemplate: `<span class="ag--overlay-loading-center" >Please wait while data is loading</span>`,
    overlayNoRowsTemplate: `<span class="ag--overlay-no-rows-center" >No Data Available</span>`,
    defaultColDef: {
      sortable: true,
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.5,
        display: "flex",
        alignItems: "center",
      },
      valueGetter: (params) => {
          if (!params.data) return "";

          const field = params.colDef.field;
          const value = params.data[field];

          return value === null || value === undefined || value === "" ? "" : value;
      },
    },
    onFilterChanged,
  }

  return (
   <>
    <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', padding: '0 10px 0 0' }}>
      <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
        <InputLabel>Jump to Column</InputLabel>
        <Select
          id="col-select"
          value={selectedColumn}
          label="Jump to Column"
          onChange={onColumnChange}
        >
          <MenuItem value="">Select Column</MenuItem>
          {colDefData?.map(col => (
            <MenuItem key={col?.field} value={col?.field}>
              {col?.headerName || col?.field}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <ButtonComponent
        maxWidth="150px"
        label="Clear All Filters"
        bgColor="black"
        color="white"
        disabled={disableFilterBtn}
        onClick={clearAllFilters}
      />
    </div>
    <DataTable
      rows={props?.rowData || []}
      columns={colDefData}
      enableCheckbox={false}
      defaultColDef={defaultColDef}
      onGridReady={onGridReady}
      gridOptions={gridOptions}
      getRowStyle={props.getRowStyle}
      selectionColumnDef={selectionColumnDef}
      onRowSelected={props.onRowSelected}
      rowSelection={props.rowSelection}
      onSelectionChanged={props.onSelectionChanged}
      enableCharts={true}
      cellSelection={true}
    />
    <div style={{marginBottom: '20px'}}></div>
   </>
  )
}

export default TargetValueDetails;